import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Stack,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  Tabs,
  Tab,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore,
  Edit,
  Delete,
  Add,
  Save,
  QuestionAnswer,
  Translate as TranslateIcon,
} from '@mui/icons-material';
import { faqPageService, globalService } from '../../services/firebase';
import { FAQPageData, FAQItem, TranslatableField } from '../../types';
import { useTranslation } from 'react-i18next';
import { getTranslatedData } from '../../utils/getTranslatedData';
import { produce } from 'immer';

const getTranslatableString = (field: TranslatableField | string | undefined, lang: string): string => {
    if (!field) return '';
    if (typeof field === 'string') {
        return lang === 'tr' ? field : '';
    }
    return field[lang] || '';
};

const ManageFAQPage: React.FC = () => {
  const [faqData, setFaqData] = useState<FAQPageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedLang, setSelectedLang] = useState('tr');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentFAQ, setCurrentFAQ] = useState<Partial<FAQItem> | null>(null);
  const { t, i18n } = useTranslation();

  const fetchFAQData = useCallback(async () => {
    setLoading(true);
    try {
      let data = await faqPageService.getFAQPageData();
      if (!data) {
        await faqPageService.initializeFAQPageData();
        data = await faqPageService.getFAQPageData();
      }
      setFaqData(data);
    } catch (err: any) {
      setError(t('faq_page_admin.errors.load', { message: err.message }));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchFAQData();
  }, [fetchFAQData]);

  const handleSaveData = async () => {
    if (!faqData) return;
    setIsSaving(true);
    setError(null);
    try {
      await faqPageService.updateFAQPageData(faqData);
      setSuccess(t('faq_page_admin.success.save'));
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(t('faq_page_admin.errors.save', { message: err.message }));
    } finally {
      setIsSaving(false);
    }
  };

  const handleHeroFieldChange = (field: 'title' | 'description', value: string) => {
    setFaqData(produce(draft => {
        if (!draft) return;
        let translatableField = draft.heroSection[field];
        if (typeof translatableField !== 'object' || translatableField === null) {
            translatableField = { tr: '', en: '', ar: '' };
        }
        translatableField[selectedLang] = value;
        draft.heroSection[field] = translatableField;
    }));
  };

  const handleTranslateHero = async () => {
    if (!faqData) return;
    setIsTranslating(true);
    try {
        const titleTr = getTranslatedData(faqData.heroSection.title, 'tr');
        const descTr = getTranslatedData(faqData.heroSection.description, 'tr');

        const [titleTranslations, descTranslations] = await Promise.all([
            titleTr ? globalService.translateTexts({ text: titleTr, sourceLang: 'tr', targetLangs: ['en', 'ar'] }) : Promise.resolve(null),
            descTr ? globalService.translateTexts({ text: descTr, sourceLang: 'tr', targetLangs: ['en', 'ar'] }) : Promise.resolve(null),
        ]);

        setFaqData(produce(draft => {
            if (!draft) return;
            if (titleTranslations) {
                draft.heroSection.title = { tr: titleTr, en: titleTranslations.en, ar: titleTranslations.ar };
            }
            if (descTranslations) {
                draft.heroSection.description = { tr: descTr, en: descTranslations.en, ar: descTranslations.ar };
            }
        }));
        setSuccess(t('translationSuccess'));
    } catch (err) {
        setError(t('translationError'));
    } finally {
        setIsTranslating(false);
    }
  };
  
  const handleOpenDialog = (faq?: FAQItem) => {
    setCurrentFAQ(faq ? JSON.parse(JSON.stringify(faq)) : {
      id: `temp-${Date.now()}`,
      question: { tr: '', en: '', ar: '' },
      answer: { tr: '', en: '', ar: '' },
      isActive: true,
      order: faqData?.faq.length ? Math.max(...faqData.faq.map(f => f.order)) + 1 : 1
    });
    setDialogOpen(true);
    setSelectedLang('tr');
  };
  
  const handleCloseDialog = () => {
      setDialogOpen(false);
      setCurrentFAQ(null);
  };

  const handleCurrentFAQChange = (field: 'question' | 'answer' | 'category', value: string) => {
    setCurrentFAQ(produce(draft => {
        if (!draft) return;
        let translatableField = (draft as any)[field];
        if (typeof translatableField !== 'object' || translatableField === null) {
            translatableField = { tr: '', en: '', ar: '' };
        }
        translatableField[selectedLang] = value;
        (draft as any)[field] = translatableField;
    }));
  };

  const handleTranslateCurrentFAQ = async () => {
    if (!currentFAQ) return;
    setIsTranslating(true);
    try {
        const questionTr = getTranslatedData(currentFAQ.question, 'tr');
        const answerTr = getTranslatedData(currentFAQ.answer, 'tr');

        const [questionTranslations, answerTranslations] = await Promise.all([
            questionTr ? globalService.translateTexts({ text: questionTr, sourceLang: 'tr', targetLangs: ['en', 'ar'] }) : Promise.resolve(null),
            answerTr ? globalService.translateTexts({ text: answerTr, sourceLang: 'tr', targetLangs: ['en', 'ar'] }) : Promise.resolve(null),
        ]);

        setCurrentFAQ(produce(draft => {
            if (!draft) return;
            if (questionTranslations) {
                draft.question = { tr: questionTr, en: questionTranslations.en, ar: questionTranslations.ar };
            }
            if (answerTranslations) {
                draft.answer = { tr: answerTr, en: answerTranslations.en, ar: answerTranslations.ar };
            }
        }));
        setSuccess(t('translationSuccess'));
    } catch(err) {
        setError(t('translationError'));
    } finally {
        setIsTranslating(false);
    }
  };

  const handleSaveFAQ = () => {
    if (!faqData || !currentFAQ || !getTranslatedData(currentFAQ.question, 'tr').trim()) {
        setError(t('faq_page_admin.errors.questionRequired'));
        return;
    };

    setFaqData(produce(draft => {
        if (!draft) return;
        const index = draft.faq.findIndex(f => f.id === currentFAQ.id);
        if (index !== -1) {
            draft.faq[index] = currentFAQ as FAQItem;
        } else {
            draft.faq.push(currentFAQ as FAQItem);
        }
        // Sıralama işlemini kaldırdık çünkü zaten render sırasında yapılıyor
    }));
    handleCloseDialog();
  };

  const handleDeleteFAQ = (faqId: string) => {
    if (!faqData || !window.confirm(t('faq_page_admin.confirmDelete'))) return;
    setFaqData(produce(draft => {
        if (draft) draft.faq = draft.faq.filter(f => f.id !== faqId);
    }));
  };

  const handleToggleFAQStatus = (faqId: string) => {
    setFaqData(produce(draft => {
        if (!draft) return;
        const faq = draft.faq.find(f => f.id === faqId);
        if(faq) faq.isActive = !faq.isActive;
    }));
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}><CircularProgress /></Box>;
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
        <Box>
            <Typography variant="h4" component="h1" gutterBottom><QuestionAnswer sx={{ mr: 1, verticalAlign: 'middle' }} />{t('faq_page_admin.title')}</Typography>
            <Typography variant="body1" color="text.secondary">{t('faq_page_admin.description')}</Typography>
        </Box>
        <Button onClick={handleSaveData} startIcon={<Save />} variant="contained" disabled={isSaving}>
            {isSaving ? <CircularProgress size={24} color="inherit" /> : t('faq_page_admin.saveAllButton')}
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h5" component="h2">{t('faq_page_admin.heroSectionTitle')}</Typography>
            <Stack direction="row" spacing={1} alignItems="center">
                <ToggleButtonGroup value={selectedLang} exclusive onChange={(_, lang) => lang && setSelectedLang(lang)} size="small">
                    <ToggleButton value="tr">TR</ToggleButton>
                    <ToggleButton value="en">EN</ToggleButton>
                    <ToggleButton value="ar">AR</ToggleButton>
                </ToggleButtonGroup>
                <Tooltip title={t('translateAll')}>
                    <span>
                    <IconButton onClick={handleTranslateHero} disabled={isTranslating || selectedLang !== 'tr'}>
                        {isTranslating ? <CircularProgress size={24}/> : <TranslateIcon />}
                    </IconButton>
                    </span>
                </Tooltip>
            </Stack>
        </Stack>
        <Stack spacing={2}>
            <TextField label={t('faq_page_admin.heroTitleLabel')} fullWidth value={getTranslatableString(faqData?.heroSection.title, selectedLang)} onChange={(e) => handleHeroFieldChange('title', e.target.value)} />
            <TextField label={t('faq_page_admin.heroDescriptionLabel')} fullWidth multiline rows={3} value={getTranslatableString(faqData?.heroSection.description, selectedLang)} onChange={(e) => handleHeroFieldChange('description', e.target.value)} />
        </Stack>
      </Paper>
      
      <Paper elevation={2} sx={{ p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h5" component="h2">{t('faq_page_admin.faqListTitle')}</Typography>
            <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()}>
                {t('faq_page_admin.addFaqButton')}
            </Button>
        </Stack>
        {faqData?.faq.slice().sort((a,b) => a.order - b.order).map(faq => (
          <Accordion key={faq.id}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Chip label={`#${faq.order}`} size="small" sx={{ mr: 2 }} />
              <Typography sx={{ flexShrink: 0, fontWeight: 500 }}>{getTranslatedData(faq.question, i18n.language) || t('faq_page_admin.untitledQuestion')}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }} dangerouslySetInnerHTML={{ __html: getTranslatedData(faq.answer, i18n.language) }} />
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <FormControlLabel control={<Switch checked={faq.isActive} onChange={() => handleToggleFAQStatus(faq.id)} />} label={t('active')} />
                    <Box>
                        <IconButton onClick={() => handleOpenDialog(faq)}><Edit /></IconButton>
                        <IconButton onClick={() => handleDeleteFAQ(faq.id)}><Delete /></IconButton>
                    </Box>
                </Stack>
              </Stack>
            </AccordionDetails>
          </Accordion>
        ))}
      </Paper>
      
      <Dialog open={dialogOpen} onClose={handleCloseDialog} fullWidth maxWidth="md">
        <DialogTitle>{currentFAQ?.id?.startsWith('temp-') ? t('faq_page_admin.dialog.addTitle') : t('faq_page_admin.dialog.editTitle')}</DialogTitle>
        <DialogContent>
            {currentFAQ && (
            <Stack spacing={2} sx={{ pt: 1 }}>
                <Tabs value={selectedLang} onChange={(_, lang) => setSelectedLang(lang)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tab label="Türkçe" value="tr" />
                    <Tab label="English" value="en" />
                    <Tab label="العربية" value="ar" />
                </Tabs>
                <TextField label={t('faq_page_admin.dialog.question')} fullWidth value={getTranslatableString(currentFAQ.question, selectedLang)} onChange={(e) => handleCurrentFAQChange('question', e.target.value)} />
                <TextField label={t('faq_page_admin.dialog.answer')} fullWidth multiline rows={5} value={getTranslatableString(currentFAQ.answer, selectedLang)} onChange={(e) => handleCurrentFAQChange('answer', e.target.value)} />
                <TextField label={t('faq_page_admin.dialog.category')} fullWidth value={getTranslatableString(currentFAQ.category, selectedLang)} onChange={(e) => handleCurrentFAQChange('category', e.target.value)} helperText={t('faq_page_admin.dialog.categoryHelper')} />
                <TextField label={t('order')} type="number" value={currentFAQ.order || 0} onChange={(e) => setCurrentFAQ(produce(draft => { if(draft) draft.order = Number(e.target.value) }))} />
                <FormControlLabel control={<Switch checked={currentFAQ.isActive || false} onChange={(e) => setCurrentFAQ(produce(draft => { if(draft) draft.isActive = e.target.checked }))} />} label={t('active')} />
            </Stack>
            )}
        </DialogContent>
        <DialogActions>
            <Tooltip title={t('translateAll')}>
                <span>
                <IconButton onClick={handleTranslateCurrentFAQ} disabled={isTranslating || selectedLang !== 'tr'}>
                    {isTranslating ? <CircularProgress size={24}/> : <TranslateIcon />}
                </IconButton>
                </span>
            </Tooltip>
            <Box sx={{ flexGrow: 1 }} />
            <Button onClick={handleCloseDialog}>{t('cancel')}</Button>
            <Button onClick={handleSaveFAQ} variant="contained">{t('save')}</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ManageFAQPage; 